import { useCallback, useEffect, useRef, useState } from 'react';
import { Camera, Loader2, Trash2, TrendingUp, TrendingDown, Wallet, Sparkles, Plus, AlertTriangle, BarChart3, Repeat, Eye, X, Check, SlidersHorizontal } from 'lucide-react';
import { toast } from 'sonner';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { nexaApi } from './api';

const SYMBOL = { ILS: '₪', USD: '$', EUR: '€', GBP: '£' };
const NEW_CAT = '__new_category__';
const cur = (c) => SYMBOL[c] || (c ? `${c} ` : '₪');
const fmt = (n) => Math.round(n).toLocaleString('en-US');
const dmy = (v) => {
  if (!v) return '';
  const s = String(v).slice(0, 10);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : s;
};
const compact = (n) => {
  const x = Math.round(Math.abs(n || 0));
  if (x >= 1000000) return `${(x / 1e6).toFixed(1)}M`;
  if (x >= 1000) return `${(x / 1000).toFixed(1)}k`;
  return String(x);
};
const monthName = (ym) => {
  const [y, m] = (ym || '').split('-');
  if (!m) return ym;
  return new Date(Number(y), Number(m) - 1, 1).toLocaleString('en-US', { month: 'short' });
};

const CAT_COLORS = ['#38bdf8', '#f472b6', '#a78bfa', '#34d399', '#fbbf24', '#fb7185', '#22d3ee', '#facc15', '#818cf8', '#4ade80'];

const SCOPES = ['personal', 'business', 'home'];
const SCOPE_META = {
  personal: { label: 'Personal', pill: 'bg-sky-400/15 text-sky-300', text: 'text-sky-300', card: 'border-sky-400/20 bg-sky-400/[0.05]' },
  business: { label: 'Business', pill: 'bg-violet-400/15 text-violet-300', text: 'text-violet-300', card: 'border-violet-400/20 bg-violet-400/[0.05]' },
  home: { label: 'Home', pill: 'bg-emerald-400/15 text-emerald-300', text: 'text-emerald-300', card: 'border-emerald-400/20 bg-emerald-400/[0.05]' },
};
const scopeMeta = (s) => SCOPE_META[s] || SCOPE_META.personal;

const PendingTxnCard = ({ label, t }) => {
  const foreign = (t.currency || 'ILS').toUpperCase() !== 'ILS';
  const sm = scopeMeta(t.scope);
  return (
    <div className="rounded-lg bg-white/[0.03] border border-white/[0.07] p-2.5">
      <p className="text-[9.5px] font-mono uppercase tracking-[0.15em] text-white/35 mb-1.5">{label}</p>
      <div className="flex flex-wrap items-center gap-1.5 mb-1.5">
        <span className={`text-[9.5px] px-1.5 py-0.5 rounded-full ${sm.pill}`}>{sm.label}</span>
        {t.category && <span className="text-[9.5px] px-1.5 py-0.5 rounded-full bg-white/[0.06] text-white/55">{t.category}</span>}
      </div>
      <p className="text-[12.5px] text-white/85 leading-snug break-words">{t.description}</p>
      <div className="flex items-center justify-between mt-1.5">
        <span className="text-[11px] text-white/40">{dmy(t.date)}</span>
        <span className="text-[13px] font-semibold text-white/85">
          {cur(t.currency)}{fmt(t.amount)}{foreign ? ` · ≈₪${fmt(t.amount_ils != null ? t.amount_ils : t.amount)}` : ''}
        </span>
      </div>
    </div>
  );
};

const resizeImg = (file) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, 2000 / Math.max(img.width, img.height));
      const c = document.createElement('canvas');
      c.width = Math.round(img.width * scale);
      c.height = Math.round(img.height * scale);
      c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
      resolve(c.toDataURL('image/jpeg', 0.9).split(',')[1]);
    };
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });

const Pie = ({ title, data, c, testid, big, onSlice }) => {
  const entries = Object.entries(data);
  const total = entries.reduce((a, [, v]) => a + v, 0);
  let acc = 0;
  const stops = entries.map(([, v], i) => {
    const start = (acc / total) * 360;
    acc += v;
    const end = (acc / total) * 360;
    return `${CAT_COLORS[i % CAT_COLORS.length]} ${start}deg ${end}deg`;
  });
  const size = big ? 'w-36 h-36' : 'w-24 h-24';
  return (
    <div className="flex flex-col items-center" data-testid={testid}>
      <p className="text-[11px] text-white/60 mb-2">{title}</p>
      {total > 0 ? (
        <>
          <div className={`${size} rounded-full`} style={{ background: `conic-gradient(${stops.join(', ')})` }}>
            <div className="w-full h-full rounded-full flex items-center justify-center" style={{ background: 'radial-gradient(circle, #0A0A0A 42%, transparent 43%)' }}>
              <span className={`${big ? 'text-[13px]' : 'text-[10px]'} text-white/80 font-semibold`}>{c}{fmt(total)}</span>
            </div>
          </div>
          <div className={`mt-2 space-y-0.5 ${big ? 'w-full max-w-[220px]' : 'w-full'}`}>
            {entries.slice(0, big ? 8 : 5).map(([cat, v], i) => (
              <button key={cat} onClick={() => onSlice && onSlice(cat)} data-testid={`nexa-pie-cat-${cat}`}
                className="flex items-center gap-1.5 text-[10px] w-full hover:bg-white/[0.04] rounded px-1 py-0.5 transition-colors">
                <span className="w-2 h-2 rounded-sm shrink-0" style={{ background: CAT_COLORS[i % CAT_COLORS.length] }} />
                <span className="text-white/55 truncate flex-1 text-left">{cat}</span>
                <span className="text-white/45">{c}{fmt(v)}</span>
                <span className="text-white/70">{Math.round((v / total) * 100)}%</span>
              </button>
            ))}
          </div>
        </>
      ) : (
        <div className={`${size} rounded-full border border-dashed border-white/10 flex items-center justify-center`}>
          <span className="text-[10px] text-white/25">no data</span>
        </div>
      )}
    </div>
  );
};

export const FinanceDetail = () => {
  const [data, setData] = useState(null);
  const [month, setMonth] = useState('');
  const [scope, setScope] = useState('personal');
  const [breakdown, setBreakdown] = useState({ personal: {}, business: {}, home: {}, currency: 'ILS' });
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null);
  const [skippedList, setSkippedList] = useState(null);
  const [pendingReview, setPendingReview] = useState(null);
  const [pendingItems, setPendingItems] = useState([]);
  const [pendingCats, setPendingCats] = useState({});
  const [activeImportId, setActiveImportId] = useState(null);
  const [recap, setRecap] = useState(null);
  const [sourceView, setSourceView] = useState(null);
  const [reviewOnly, setReviewOnly] = useState(false);
  const [postImport, setPostImport] = useState(false);
  const [catFilter, setCatFilter] = useState(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [search, setSearch] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [rules, setRules] = useState([]);
  const [showRules, setShowRules] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [catInfo, setCatInfo] = useState([]);
  const [cascade, setCascade] = useState(null);
  const [showCats, setShowCats] = useState(false);
  const [insights, setInsights] = useState('');
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [trends, setTrends] = useState([]);
  const [budgets, setBudgets] = useState([]);
  const [suggestions, setSuggestions] = useState(null);
  const [recurring, setRecurring] = useState({ recurring: [], monthly_total: 0, currency: 'ILS' });
  const fileRef = useRef(null);

  const load = useCallback((m, sc) => {
    const params = new URLSearchParams();
    if (m) params.set('month', m);
    if (sc && sc !== 'all') params.set('scope', sc);
    const qs = params.toString() ? `?${params.toString()}` : '';
    const scopeQs = sc && sc !== 'all' ? `?scope=${sc}` : '';
    nexaApi.get(`/finance${qs}`).then(({ data: d }) => setData(d)).catch(() => {});
    nexaApi.get(`/finance/breakdown${m ? `?month=${m}` : ''}`).then(({ data: d }) => setBreakdown(d)).catch(() => {});
    nexaApi.get(`/finance/trends${scopeQs}`).then(({ data: d }) => setTrends(d.trends || [])).catch(() => {});
    nexaApi.get(`/finance/recurring${scopeQs}`).then(({ data: d }) => setRecurring(d)).catch(() => {});
    nexaApi.get('/finance/budgets').then(({ data: d }) => setBudgets(d.budgets || [])).catch(() => {});
    nexaApi.get('/finance/vendor-rules').then(({ data: d }) => setRules(d.rules || [])).catch(() => {});
    nexaApi.get('/finance/pending').then(({ data: d }) => setPendingItems(d.pending || [])).catch(() => {});
    loadCategories();
  }, []);

  const loadCategories = useCallback(() => {
    nexaApi.get('/finance/categories').then(({ data: d }) => setCatInfo(d.catinfo || [])).catch(() => {});
  }, []);

  const setBudget = async (category) => {
    const v = window.prompt(`Monthly budget for ${category} (0 to remove):`);
    if (v === null) return;
    const limit = parseFloat(v);
    if (Number.isNaN(limit)) return;
    await nexaApi.put('/finance/budget', { category, limit });
    toast.success(limit > 0 ? 'Budget set' : 'Budget removed');
    load(month, scope);
  };

  const loadSuggestions = async () => {
    try {
      const { data: d } = await nexaApi.get('/finance/budgets/suggest');
      setSuggestions(d.suggestions || []);
    } catch {
      toast.error('Could not load suggestions');
    }
  };

  const applySuggestion = async (sg) => {
    await nexaApi.put('/finance/budget', { category: sg.category, limit: sg.suggested });
    toast.success(`${sg.category} capped at ${sg.suggested}`);
    nexaApi.get('/finance/budgets').then(({ data: d }) => setBudgets(d.budgets || [])).catch(() => {});
    setSuggestions((prev) => (prev || []).map((s) => (s.category === sg.category ? { ...s, has_budget: true } : s)));
  };

  const applyAllSuggestions = async () => {
    if (!suggestions || !suggestions.length) return;
    await Promise.all(suggestions.map((sg) => nexaApi.put('/finance/budget', { category: sg.category, limit: sg.suggested })));
    toast.success(`Applied ${suggestions.length} budget${suggestions.length > 1 ? 's' : ''}`);
    nexaApi.get('/finance/budgets').then(({ data: d }) => setBudgets(d.budgets || [])).catch(() => {});
    setSuggestions((prev) => (prev || []).map((s) => ({ ...s, has_budget: true })));
  };

  useEffect(() => { load(month, scope); }, [month, scope, load]);

  const onFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setBusy(true);
    toast.info(`Reading ${files.length} file${files.length > 1 ? 's' : ''}…`);
    try {
      const imgFiles = files.filter((f) => f.type.startsWith('image/'));
      const pdfFiles = files.filter((f) => f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf'));
      const images_base64 = await Promise.all(imgFiles.map(resizeImg));
      const readB64 = (file) => new Promise((res, rej) => {
        const r = new FileReader();
        r.onloadend = () => res(String(r.result).split(',')[1]);
        r.onerror = rej;
        r.readAsDataURL(file);
      });
      const pdfs_base64 = await Promise.all(pdfFiles.map(readB64));
      const payload = JSON.stringify({ images_base64, pdfs_base64, scope });
      const b64 = btoa(unescape(encodeURIComponent(payload)));
      const CHUNK = 500 * 1024;
      const total = Math.max(1, Math.ceil(b64.length / CHUNK));
      const uploadId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      let res = null;
      for (let i = 0; i < total; i += 1) {
        const resp = await nexaApi.post('/finance/ingest/chunk', {
          upload_id: uploadId, index: i, total, data: b64.slice(i * CHUNK, (i + 1) * CHUNK),
        });
        res = resp.data;
      }
      if (res?.job_id) {
        const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
        let count = null;
        let pending = 0;
        let importId = null;
        setProgress({ status: 'processing', found: 0, done: 0, total: 0 });
        setSkippedList(null);
        for (let tries = 0; tries < 120; tries += 1) {
          await sleep(2500);
          const { data: s } = await nexaApi.get(`/finance/ingest/status/${res.job_id}`);
          setProgress({
            status: s.status, found: s.found || 0,
            done: s.done_batches || 0, total: s.total_batches || 0,
          });
          if (s.status === 'done') { count = s.count; pending = s.pending || 0; importId = s.import_id; break; }
          if (s.status === 'error' || s.status === 'unknown') break;
        }
        setProgress(null);
        if (count !== null) {
          if (count > 0) {
            toast.success(`Added ${count} new charge${count > 1 ? 's' : ''}`);
            setMonth('');
            setPostImport(true);
          } else if (pending === 0) {
            toast.info('No transactions found in that file');
          }
          load('', scope);
          if (pending > 0) {
            setActiveImportId(importId);
            try {
              const { data: p } = await nexaApi.get(`/finance/pending?import_id=${importId}`);
              if (p.pending && p.pending.length) {
                setPendingReview({ importId, items: p.pending });
                toast.info(`${p.pending.length} need${p.pending.length > 1 ? '' : 's'} your review`);
              } else {
                showRecap(importId);
              }
            } catch { showRecap(importId); }
          } else {
            showRecap(importId);
          }
        } else {
          toast.error('Could not read the file — please try again');
        }
      } else {
        toast.error('Could not read the file');
      }
    } catch {
      toast.error('Could not read the images');
    } finally {
      setBusy(false);
      setProgress(null);
    }
  };

  const addRule = async () => {
    const match = window.prompt('When a vendor name contains… (e.g. "emergent")');
    if (!match || !match.trim()) return;
    const cat = window.prompt(`…file it under which category?\nOptions: ${(data?.categories || []).join(', ')}`, 'Subscriptions');
    if (!cat) return;
    await nexaApi.post('/finance/vendor-rules', { match: match.trim(), category: cat.trim() });
    toast.success('Vendor rule saved');
    load(month, scope);
  };
  const removeRule = async (id) => {
    await nexaApi.delete(`/finance/vendor-rules/${id}`);
    load(month, scope);
  };

  const viewSource = async (t) => {
    setSourceView({ loading: true });
    try {
      const { data } = await nexaApi.get(`/finance/source/${t.id}`);
      setSourceView({ loading: false, ...data, label: t.description });
    } catch {
      setSourceView(null);
      toast.error('Source not available for this entry');
    }
  };

  const changeCat = async (t, category) => {
    if (category === t.category) return;
    await nexaApi.patch(`/finance/transaction/${t.id}`, { category });
    // Learn from me: offer to make it a vendor rule so it sticks next time.
    const match = (t.description || '').trim().split(/\s+/).slice(0, 2).join(' ');
    toast.success(`Filed under ${category}`, {
      action: match ? {
        label: `Always “${match}”`,
        onClick: async () => {
          await nexaApi.post('/finance/vendor-rules', { match, category });
          toast.success(`Rule saved — “${match}” → ${category}`);
          load(month, scope);
        },
      } : undefined,
    });
    load(month, scope);
    // Cascade: other lines with the exact same name but a different category — ask to switch them too.
    try {
      const { data: s } = await nexaApi.post('/finance/category/similar', {
        description: t.description, category, exclude_id: t.id,
      });
      if (s.matches && s.matches.length) {
        setCascade({ category, name: t.description, matches: s.matches, selected: new Set(s.matches.map((m) => m.id)) });
      }
    } catch { /* ignore */ }
  };

  const createCategory = async (name) => {
    const n = (name || '').trim();
    if (!n) return null;
    try {
      const { data: r } = await nexaApi.post('/finance/categories', { name: n });
      setData((d) => (d ? { ...d, categories: r.categories } : d));
      loadCategories();
      return r.name;
    } catch { toast.error('Could not create category'); return null; }
  };
  const onCatSelect = async (value, apply) => {
    if (value === NEW_CAT) {
      const name = window.prompt('New category name (type anything you like):');
      const created = await createCategory(name);
      if (created) apply(created);
      return;
    }
    apply(value);
  };

  const renameCat = async (name) => {
    const v = window.prompt(`Rename "${name}" to:`, name);
    if (!v || !v.trim() || v.trim() === name) return;
    try {
      const { data: r } = await nexaApi.put('/finance/categories/rename', { old: name, new: v.trim() });
      toast.success(r.merged ? `Merged into ${r.name}` : `Renamed to ${r.name}`);
      load(month, scope);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not rename'); }
  };
  const mergeCat = async (source, target) => {
    if (!target || target === source) return;
    try {
      await nexaApi.post('/finance/categories/merge', { source, target });
      toast.success(`Merged "${source}" into ${target}`);
      load(month, scope);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not merge'); }
  };
  const deleteCat = async (c) => {
    const msg = c.count > 0
      ? `Delete "${c.name}"? Its ${c.count} transaction${c.count === 1 ? '' : 's'} move to "Other".`
      : `Delete "${c.name}"? It isn't used anywhere.`;
    if (!window.confirm(msg)) return;
    try {
      await nexaApi.delete(`/finance/categories/${encodeURIComponent(c.name)}`);
      toast.success(`Deleted "${c.name}"`);
      load(month, scope);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not delete'); }
  };

  const toggleCascade = (id) => setCascade((c) => {
    if (!c) return c;
    const sel = new Set(c.selected);
    if (sel.has(id)) sel.delete(id); else sel.add(id);
    return { ...c, selected: sel };
  });
  const applyCascade = async () => {
    const ids = cascade ? [...cascade.selected] : [];
    if (ids.length) {
      try {
        const { data: r } = await nexaApi.post('/finance/category/apply-similar', { ids, category: cascade.category });
        toast.success(`Updated ${r.updated} more line${r.updated === 1 ? '' : 's'}`);
        load(month, scope);
      } catch { toast.error('Could not update the others'); }
    }
    setCascade(null);
  };

  const showRecap = async (importId) => {
    setActiveImportId(null);
    try {
      const { data: r } = await nexaApi.get(`/finance/import-recap/${importId}`);
      if (r && r.count > 0) setRecap(r);
    } catch { /* ignore */ }
  };

  const resolvePending = async (item, action) => {
    const category = action === 'add' ? (pendingCats[item.id] || item.new.category) : undefined;
    const norm = (s) => (s || '').trim().toLowerCase();
    // Same product appearing more than once in this import → apply the choice to every
    // identical uncertain line at once, so NEXA never asks about the same thing twice.
    const siblings = (action === 'add' && category && item.kind === 'uncertain' && pendingReview)
      ? pendingReview.items.filter((x) => x.id !== item.id && x.kind === 'uncertain'
          && norm(x.new?.description) === norm(item.new?.description))
      : [];
    const targets = [item, ...siblings];
    try {
      for (const it of targets) {
        const cat = action === 'add' ? (pendingCats[it.id] || category || it.new.category) : undefined;
        await nexaApi.post('/finance/pending/resolve', { id: it.id, action, category: cat });
      }
    } catch { toast.error('Could not save that'); return; }
    // Picked a category for an uncategorised line → NEXA now remembers it for this exact vendor/product.
    if (action === 'add' && category && item.kind === 'uncertain') {
      const extra = siblings.length ? ` (+${siblings.length} identical)` : '';
      toast.success(`Remembered — future “${item.new.description}” → ${category}${extra}`);
      nexaApi.get('/finance/vendor-rules').then(({ data: d }) => setRules(d.rules || [])).catch(() => {});
    }
    const doneIds = new Set(targets.map((t) => t.id));
    setPendingItems((list) => list.filter((x) => !doneIds.has(x.id)));
    const remaining = pendingReview ? pendingReview.items.filter((x) => !doneIds.has(x.id)) : [];
    if (!remaining.length) {
      setPendingReview(null);
      load('', scope);
      if (activeImportId) showRecap(activeImportId);
    } else {
      setPendingReview((pr) => (pr ? { ...pr, items: remaining } : pr));
    }
  };
  const resolveAllPending = async (action) => {
    const importId = pendingReview?.importId || activeImportId;
    try {
      await nexaApi.post('/finance/pending/resolve-all', { import_id: importId, action });
    } catch { toast.error('Could not save'); return; }
    toast.success(action === 'add' ? 'Added everything' : 'Skipped everything');
    setPendingReview(null);
    setPendingItems((list) => (importId ? list.filter((x) => x.import_id !== importId) : []));
    load('', scope);
    if (activeImportId) showRecap(activeImportId);
  };

  const askNexaReview = async () => {
    try {
      const { data: r } = await nexaApi.post('/finance/review/ask', { scope: scope === 'all' ? null : scope });
      if (r.count > 0) toast.success(`Nexa sent you a list of ${r.count} in chat`);
      else toast.info('Nothing uncertain to review — all sorted');
    } catch { toast.error('Could not reach Nexa'); }
  };

  const applyAutoRule = async (match, category) => {
    await nexaApi.post('/finance/vendor-rules', { match, category, scope: scope === 'all' ? null : scope, apply_existing: true });
    toast.success(`“${match}” → ${category} · remembered for next time`);
    load(month, scope);
  };
  const editRate = async (t) => {
    const currentIls = t.amount_ils != null ? t.amount_ils : t.amount;
    const v = window.prompt(
      `${t.description}\n${t.amount} ${t.currency} — set the ₪ (ILS) value your bank actually charged:`,
      String(currentIls));
    if (v === null) return;
    const num = parseFloat(v);
    if (Number.isNaN(num) || num < 0) { toast.error('Enter a valid number'); return; }
    await nexaApi.patch(`/finance/transaction/${t.id}`, { amount_ils: num });
    toast.success('Rate updated');
    load(month, scope);
  };
  const toggleType = async (t) => {
    await nexaApi.patch(`/finance/transaction/${t.id}`, { type: t.type === 'income' ? 'expense' : 'income' });
    load(month, scope);
  };
  const remove = async (id) => {
    await nexaApi.delete(`/finance/transaction/${id}`);
    load(month, scope);
  };

  const getInsights = async () => {
    setInsightsLoading(true);
    try {
      const { data: d } = await nexaApi.get(`/finance/insights${month ? `?month=${month}` : ''}`);
      setInsights(d.insights || '');
    } finally { setInsightsLoading(false); }
  };

  if (!data) return <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-white/30" /></div>;

  const s = data.summary;
  const c = cur(s.currency);
  const maxCat = Math.max(1, ...Object.values(s.by_category));

  return (
    <div className="space-y-6" data-testid="nexa-finance">
      <input ref={fileRef} type="file" accept="image/*,application/pdf,.pdf" multiple className="hidden"
        data-testid="nexa-finance-input"
        onChange={(e) => { onFiles(e.target.files); e.target.value = ''; }} />

      {/* Sticky action bar */}
      <div className="sticky top-2 z-30 rounded-2xl bg-black/70 backdrop-blur-xl border border-white/10 px-2.5 py-2.5 flex flex-wrap items-center gap-2 shadow-[0_8px_32px_rgba(0,0,0,0.4)]" data-testid="nexa-finance-topbar">
        <div className="grid grid-cols-4 gap-1 p-1 rounded-xl bg-white/[0.04] border border-white/10">
          {[['personal', 'Personal'], ['business', 'Business'], ['home', 'Home'], ['all', 'All']].map(([v, lbl]) => (
            <button key={v} onClick={() => setScope(v)} data-testid={`nexa-finance-scope-${v}`}
              className={`h-8 px-2.5 rounded-lg text-[12px] font-medium transition-colors ${scope === v ? 'bg-sky-400 text-black' : 'text-white/55 hover:text-white/80'}`}>{lbl}</button>
          ))}
        </div>
        {data.months.length > 0 && (
          <select value={month} onChange={(e) => setMonth(e.target.value)} data-testid="nexa-finance-month-select"
            className="h-9 bg-white/[0.05] border border-white/10 rounded-xl text-[12px] text-white/75 px-2.5 outline-none">
            <option value="" className="bg-black">All months</option>
            {data.months.map((m) => <option key={m} value={m} className="bg-black">{monthName(m)}</option>)}
          </select>
        )}
        <div className="flex items-center gap-2 ml-auto">
          <button type="button" onClick={() => fileRef.current?.click()} disabled={busy} data-testid="nexa-finance-upload"
            className="h-9 px-3.5 rounded-xl border border-sky-400/30 bg-sky-400/10 text-sky-200 flex items-center gap-2 text-[12.5px] font-medium active:scale-95 disabled:opacity-50 transition-transform">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}<span className="hidden sm:inline">{busy ? 'Reading…' : 'Import'}</span>
          </button>
          <button type="button" onClick={() => setShowAdd((v) => !v)} data-testid="nexa-finance-add-top"
            className="h-9 px-3 rounded-xl border border-white/12 bg-white/[0.04] text-white/80 flex items-center gap-1.5 text-[12.5px] active:scale-95 transition-transform"><Plus className="w-4 h-4" /><span className="hidden sm:inline">Add</span></button>
          <button type="button" onClick={() => setShowSettings(true)} data-testid="nexa-finance-settings" title="Rules & categories"
            className="h-9 w-9 rounded-xl border border-white/12 bg-white/[0.04] text-white/70 hover:text-white flex items-center justify-center active:scale-95 transition-transform"><SlidersHorizontal className="w-4 h-4" /></button>
        </div>
      </div>

      {/* Import progress */}
      {progress && (
        <div className="rounded-2xl border border-sky-400/25 bg-sky-400/[0.06] p-4" data-testid="nexa-finance-progress">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[12px] text-sky-100 flex items-center gap-2">
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              Reading your file…
            </span>
            <span className="text-[12px] text-sky-200 font-medium" data-testid="nexa-progress-count">
              {progress.found} found{progress.skipped ? ` · ${progress.skipped} dup` : ''}
            </span>
          </div>
          <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
            <div
              className="h-full bg-sky-400 rounded-full transition-all duration-300"
              style={{ width: `${progress.total ? Math.round((progress.done / progress.total) * 100) : (progress.found ? 60 : 15)}%` }}
            />
          </div>
          {progress.total > 0 && (
            <p className="text-[10px] text-white/40 mt-1.5">Analyzing section {progress.done} of {progress.total}</p>
          )}
        </div>
      )}

      {/* Auto-rule offer after an import — vendors that landed in Uncategorized */}
      {postImport && (() => {
        const groups = {};
        data.transactions.filter((t) => t.category === 'Uncategorized').forEach((t) => {
          const full = (t.description || '').trim();
          if (!full) return;
          const key = full.toLowerCase();
          if (!groups[key]) groups[key] = { label: full, n: 0 };
          groups[key].n += 1;
        });
        const vendors = Object.values(groups).sort((a, b) => b.n - a.n).slice(0, 12);
        if (vendors.length === 0) return null;
        return (
          <div className="rounded-2xl border border-sky-400/25 bg-sky-400/[0.05] p-4" data-testid="nexa-autorule">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[11px] font-mono tracking-[0.2em] uppercase text-sky-200/80">Teach me these {vendors.length} vendor{vendors.length > 1 ? 's' : ''}</span>
              <button onClick={() => setPostImport(false)} data-testid="nexa-autorule-dismiss" className="text-[11px] text-white/40 hover:text-white/70">Later</button>
            </div>
            <p className="text-[11px] text-white/40 mb-3 leading-relaxed">
              I couldn’t place these confidently. Pick a category and I’ll fix them all now and remember the vendor for next time.
            </p>
            <div className="space-y-2">
              {vendors.map(({ label, n }) => (
                <div key={label} data-testid={`nexa-autorule-${label}`} className="flex items-center gap-2 rounded-xl border border-white/[0.07] bg-white/[0.02] px-3 py-2">
                  <span className="text-[12.5px] text-white/80 break-words flex-1" title={label} dir="auto">{label} <span className="text-white/35">×{n}</span></span>
                  <select defaultValue="" onChange={(e) => { if (e.target.value) onCatSelect(e.target.value, (c) => applyAutoRule(label, c)); }}
                    data-testid={`nexa-autorule-select-${label}`}
                    className="bg-black border border-white/15 rounded-lg text-[11.5px] text-white/80 px-2 py-1.5 shrink-0">
                    <option value="" disabled className="bg-black">Category…</option>
                    {(data.categories || []).filter((cc) => cc !== 'Uncategorized').map((cc) => <option key={cc} value={cc} className="bg-black">{cc}</option>)}
                    <option value={NEW_CAT} className="bg-black text-sky-300">＋ New category…</option>
                  </select>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Pending review banner — charges waiting for a decision */}
      {pendingItems.length > 0 && !pendingReview && (
        <div className="rounded-2xl border border-amber-400/30 bg-amber-400/[0.06] p-4 flex items-center justify-between gap-3" data-testid="nexa-pending-banner">
          <div className="min-w-0">
            <p className="text-[13px] text-white/85 font-medium">{pendingItems.length} charge{pendingItems.length > 1 ? 's' : ''} waiting for your review</p>
            <p className="text-[11px] text-white/45 mt-0.5">Possible duplicates or ones I couldn’t categorise — nothing’s saved until you decide.</p>
          </div>
          <button onClick={() => setPendingReview({ importId: null, items: pendingItems })} data-testid="nexa-pending-open"
            className="shrink-0 h-9 px-4 rounded-xl bg-amber-400 text-black font-semibold text-[12.5px] active:scale-95">Review</button>
        </div>
      )}

      {/* Duplicate review — what the last import skipped */}
      {skippedList && skippedList.length > 0 && (
        <div className="rounded-2xl border border-amber-400/25 bg-amber-400/[0.05] p-4" data-testid="nexa-finance-skipped">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] font-mono tracking-[0.2em] uppercase text-amber-200/80">
              Skipped {skippedList.length} duplicate{skippedList.length > 1 ? 's' : ''}
            </span>
            <button onClick={() => setSkippedList(null)} data-testid="nexa-skipped-dismiss"
              className="text-[11px] text-white/40 hover:text-white/70">Dismiss</button>
          </div>
          <p className="text-[11px] text-white/40 mb-2.5 leading-relaxed">
            Already in your books, so I didn’t add them again. Scan for anything that should actually be here.
          </p>
          <div className="space-y-1 max-h-52 overflow-y-auto">
            {skippedList.map((it, i) => (
              <div key={i} data-testid={`nexa-skipped-${i}`}
                className="flex items-center justify-between text-[12px] rounded-lg bg-white/[0.02] px-2.5 py-1.5">
                <span className="text-white/60 truncate mr-2">
                  <span className="text-white/35">{dmy(it.date)}</span> {it.description}
                </span>
                <span className="text-white/70 shrink-0">{cur(it.currency)}{fmt(it.amount)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Personal / Business scope + Month → moved to sticky top bar */}

      {/* Summary (KPI row) */}
      <div className="grid grid-cols-3 gap-2.5">
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-3" data-testid="nexa-finance-income">
          <div className="flex items-center gap-1 text-emerald-400 mb-1"><TrendingUp className="w-3.5 h-3.5" /><span className="text-[10px] uppercase tracking-wider">In</span></div>
          <p className="text-[17px] font-semibold text-white">{c}{fmt(s.income)}</p>
        </div>
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-3" data-testid="nexa-finance-expense">
          <div className="flex items-center gap-1 text-rose-400 mb-1"><TrendingDown className="w-3.5 h-3.5" /><span className="text-[10px] uppercase tracking-wider">Out</span></div>
          <p className="text-[17px] font-semibold text-white">{c}{fmt(s.expense)}</p>
        </div>
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-3" data-testid="nexa-finance-net">
          <div className="flex items-center gap-1 text-sky-400 mb-1"><Wallet className="w-3.5 h-3.5" /><span className="text-[10px] uppercase tracking-wider">Net</span></div>
          <p className={`text-[17px] font-semibold ${s.net >= 0 ? 'text-white' : 'text-rose-300'}`}>{c}{fmt(s.net)}</p>
        </div>
      </div>

      {/* Multi-currency breakdown (totals shown in ILS, converted at each day's rate) */}
      {s.by_currency && Object.keys(s.by_currency).length > 0 && (
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-3.5" data-testid="nexa-finance-currencies">
          <p className="text-[10px] font-mono tracking-[0.2em] uppercase text-white/40 mb-2">
            Foreign spend — totals above converted to ₪ at each day's rate
          </p>
          <div className="flex flex-wrap gap-2">
            {Object.entries(s.by_currency).map(([code, amt]) => (
              <span key={code} data-testid={`nexa-currency-${code}`}
                className="px-2.5 py-1 rounded-lg bg-white/[0.05] border border-white/10 text-[12px] text-white/75">
                <span className="text-white/45">{code}</span> {fmt(Math.abs(amt))}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Analytics + Ledger bento — wide desktop uses two columns; mobile stays stacked */}
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-4 lg:gap-6 items-start">
      <div className="xl:col-span-5 flex flex-col gap-5 min-w-0">

      {/* This month vs last month */}
      {(() => {
        if (trends.length < 2) return null;
        const cM = trends[trends.length - 1];
        const pM = trends[trends.length - 2];
        const a = cM.expense || 0;
        const b = pM.expense || 0;
        if (!a && !b) return null;
        const pct = b > 0 ? Math.round(((a - b) / b) * 100) : (a > 0 ? 100 : 0);
        const up = a > b;
        return (
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4" data-testid="nexa-finance-compare">
            <p className="text-[11px] font-mono tracking-[0.2em] uppercase text-white/50 mb-3">This month vs last</p>
            <div className="flex items-stretch gap-3">
              <div className="flex-1">
                <p className="text-[10px] text-white/40 uppercase tracking-wide">{monthName(cM.month)}</p>
                <p className="text-[18px] font-semibold text-white">{cur(data.summary.currency)}{fmt(a)}</p>
              </div>
              <div className="w-px bg-white/10" />
              <div className="flex-1">
                <p className="text-[10px] text-white/40 uppercase tracking-wide">{monthName(pM.month)}</p>
                <p className="text-[18px] font-semibold text-white/60">{cur(data.summary.currency)}{fmt(b)}</p>
              </div>
              <div className={`flex flex-col items-end justify-center px-3 rounded-xl ${up ? 'bg-rose-400/10 text-rose-300' : 'bg-emerald-400/10 text-emerald-300'}`}>
                {up ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                <span className="text-[13px] font-semibold" data-testid="nexa-compare-pct">{up ? '+' : ''}{pct}%</span>
              </div>
            </div>
            <p className="text-[10.5px] text-white/35 mt-2">
              {up ? `You've spent ${cur(data.summary.currency)}${fmt(a - b)} more than last month.` : (a < b ? `Nice — ${cur(data.summary.currency)}${fmt(b - a)} less than last month.` : 'Same as last month.')}
            </p>
          </div>
        );
      })()}

      {/* Monthly trends — scrollable, ~3 months in view, with amounts */}
      {trends.some((t) => t.income || t.expense) && (
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4" data-testid="nexa-finance-trends">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 text-white/50"><BarChart3 className="w-4 h-4 text-sky-400" /><span className="text-[11px] font-mono tracking-[0.2em] uppercase">12-month trend</span></div>
            <span className="text-[9px] text-white/25 tracking-wide">swipe ↔</span>
          </div>
          <div className="overflow-x-auto -mx-1 px-1 pb-1 nexa-hide-scroll" data-testid="nexa-trends-scroll">
            <div className="flex gap-2">
              {trends.map((t) => {
                const max = Math.max(1, ...trends.flatMap((x) => [x.income, x.expense]));
                const net = (t.income || 0) - (t.expense || 0);
                const activeM = month === t.month;
                return (
                  <button key={t.month} onClick={() => { const nm = activeM ? '' : t.month; setMonth(nm); load(nm, scope); }}
                    data-testid={`nexa-trend-month-${t.month}`}
                    className={`shrink-0 w-[31%] min-w-[96px] flex flex-col items-center gap-1.5 rounded-xl py-1.5 transition-colors ${activeM ? 'bg-sky-400/10 ring-1 ring-sky-400/40' : 'hover:bg-white/[0.03]'}`}>
                    <div className="flex items-end justify-center gap-2 h-28 w-full">
                      {['income', 'expense'].map((kind) => {
                        const v = t[kind];
                        const inc = kind === 'income';
                        return (
                          <div key={kind} className="flex flex-col items-center justify-end h-full">
                            <span className={`text-[9.5px] font-medium mb-1 ${inc ? 'text-emerald-300/90' : 'text-rose-300/90'} ${v ? '' : 'opacity-0'}`}>
                              ₪{compact(v)}
                            </span>
                            <div className={`w-5 rounded-t ${inc ? 'bg-emerald-400/80' : 'bg-rose-400/80'}`}
                              style={{ height: `${v ? Math.max(6, (v / max) * 100) : 0}%` }}
                              title={`${inc ? 'In' : 'Out'} ₪${fmt(v)}`} />
                          </div>
                        );
                      })}
                    </div>
                    <span className="text-[10.5px] text-white/45">{monthName(t.month)}</span>
                    {(t.income || t.expense) ? (
                      <span data-testid={`nexa-trend-net-${t.month}`}
                        className={`text-[10px] font-semibold ${net >= 0 ? 'text-emerald-300' : 'text-rose-300'}`}
                        title={`Net ₪${fmt(net)}`}>
                        {net >= 0 ? '+' : '−'}₪{compact(net)}
                      </span>
                    ) : <span className="text-[10px] opacity-0">·</span>}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="flex gap-4 mt-2 text-[10px] text-white/40">
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-emerald-400/80" /> Income</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-rose-400/80" /> Expenses</span>
            <span className="text-white/30">± Net</span>
          </div>
        </div>
      )}

      {/* Recurring / subscriptions detector */}
      {recurring.recurring.length > 0 && (
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4" data-testid="nexa-finance-recurring">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 text-white/50">
              <Repeat className="w-4 h-4 text-violet-400" />
              <span className="text-[11px] font-mono tracking-[0.2em] uppercase">Recurring</span>
            </div>
            {recurring.monthly_total > 0 && (
              <span className="text-[12px] text-white/60" data-testid="nexa-recurring-monthly">
                ~<span className="text-white/90 font-semibold">{cur(recurring.currency)}{fmt(recurring.monthly_total)}</span>/mo
              </span>
            )}
          </div>
          <p className="text-[11px] text-white/35 mb-3 leading-relaxed">
            Charges I’ve seen repeat across months. Check for anything you forgot you’re still paying for.
          </p>
          <div className="space-y-2">
            {recurring.recurring.map((r, i) => (
              <div key={`${r.name}-${i}`} data-testid={`nexa-recurring-${i}`}
                className="rounded-xl border border-white/[0.07] bg-white/[0.02] p-3 flex items-center gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-[13.5px] text-white/85 truncate">{r.name}</span>
                    {!r.active && (
                      <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-400/15 text-amber-300 shrink-0"
                        data-testid={`nexa-recurring-stale-${i}`}>not charged lately</span>
                    )}
                  </div>
                  <p className="text-[10.5px] text-white/40 mt-0.5">
                    {r.category} · seen {r.months} month{r.months > 1 ? 's' : ''} · last {r.last_date}
                  </p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-[13.5px] text-white/90 font-medium">{cur(r.currency)}{fmt(r.amount)}<span className="text-white/40 text-[10px]">/mo</span></p>
                  <p className="text-[10px] text-white/35">{cur(r.currency)}{fmt(r.total_paid)} total</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Budgets */}
      <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4" data-testid="nexa-finance-budgets">
        <div className="flex items-center justify-between mb-3">
          <span className="text-[11px] font-mono tracking-[0.2em] uppercase text-white/50">Budgets (this month)</span>
          <div className="flex items-center gap-3">
            <button onClick={loadSuggestions} data-testid="nexa-budget-suggest"
              className="flex items-center gap-1 text-[11px] text-violet-300 active:scale-95"><Sparkles className="w-3.5 h-3.5" /> Suggest</button>
            <button onClick={() => { const cat = window.prompt(`Category to budget? One of:\n${data.categories.join(', ')}`); if (cat && data.categories.includes(cat)) setBudget(cat); else if (cat) toast.error('Unknown category'); }}
              data-testid="nexa-finance-budget-add" className="flex items-center gap-1 text-[11px] text-sky-400 active:scale-95"><Plus className="w-3.5 h-3.5" /> Set</button>
          </div>
        </div>
        {suggestions && (
          <div className="rounded-xl border border-violet-400/25 bg-violet-400/[0.05] p-3 mb-3" data-testid="nexa-budget-suggestions">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[11px] text-violet-200/80">Suggested caps (from your 3-month average)</span>
              <div className="flex gap-3">
                <button onClick={applyAllSuggestions} data-testid="nexa-suggest-apply-all" className="text-[11px] text-violet-200 hover:text-white">Apply all</button>
                <button onClick={() => setSuggestions(null)} className="text-[11px] text-white/40 hover:text-white/70">Close</button>
              </div>
            </div>
            {suggestions.length === 0 ? (
              <p className="text-[11px] text-white/40">Not enough history yet — I need a couple of months of spend.</p>
            ) : (
              <div className="space-y-1.5">
                {suggestions.map((sg) => (
                  <div key={sg.category} data-testid={`nexa-suggest-${sg.category}`} className="flex items-center justify-between text-[12px]">
                    <span className="text-white/70 truncate flex-1">{sg.category} <span className="text-white/35">avg {c}{fmt(sg.avg)}</span></span>
                    <button onClick={() => applySuggestion(sg)} className="text-violet-200 hover:text-white shrink-0 px-2 py-0.5 rounded-lg border border-violet-400/25">
                      cap {c}{fmt(sg.suggested)}{sg.has_budget ? ' ✓' : ''}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        {budgets.length === 0 ? (
          <p className="text-[12px] text-white/30">No budgets yet. Set a cap on a category and I’ll warn you when you’re close.</p>
        ) : (
          <div className="space-y-2.5">
            {budgets.map((b) => (
              <div key={b.category} data-testid={`nexa-budget-${b.category}`} onClick={() => setBudget(b.category)} className="cursor-pointer">
                <div className="flex justify-between text-[12.5px] mb-1">
                  <span className={`flex items-center gap-1 ${b.over ? 'text-rose-300' : 'text-white/70'}`}>
                    {b.over && <AlertTriangle className="w-3.5 h-3.5" />}{b.category}
                  </span>
                  <span className={b.over ? 'text-rose-300 font-medium' : 'text-white/60'}>{c}{fmt(b.spent)} / {c}{fmt(b.limit)}</span>
                </div>
                <div className="h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
                  <div className={`h-full rounded-full ${b.over ? 'bg-rose-400' : b.pct > 80 ? 'bg-amber-400' : 'bg-emerald-400'}`} style={{ width: `${Math.min(100, b.pct)}%` }} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Vendor rules + Category manager → moved into the Settings sheet (gear icon) */}


      {/* Expense-type pie(s): follows the Personal/Business/Home/All toggle */}
      {(Object.keys(breakdown.personal).length > 0 || Object.keys(breakdown.business).length > 0 || Object.keys(breakdown.home || {}).length > 0) && (
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4" data-testid="nexa-finance-pies">
          <p className="text-[11px] font-mono tracking-[0.2em] uppercase text-white/50 mb-3">Expense types</p>
          {scope === 'all' ? (
            <div className="grid grid-cols-3 gap-3">
              <Pie title="Personal" data={breakdown.personal} c={cur(breakdown.currency)} testid="nexa-pie-personal" onSlice={setCatFilter} />
              <Pie title="Business" data={breakdown.business} c={cur(breakdown.currency)} testid="nexa-pie-business" onSlice={setCatFilter} />
              <Pie title="Home" data={breakdown.home || {}} c={cur(breakdown.currency)} testid="nexa-pie-home" onSlice={setCatFilter} />
            </div>
          ) : (
            <div className="flex justify-center">
              <Pie title={scopeMeta(scope).label}
                data={breakdown[scope] || {}}
                c={cur(breakdown.currency)} testid={`nexa-pie-${scope}`} big onSlice={setCatFilter} />
            </div>
          )}
        </div>
      )}

      {/* Insights */}
      <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2 text-amber-300"><Sparkles className="w-4 h-4" /><span className="text-[11px] font-mono tracking-[0.2em] uppercase text-white/50">Her take</span></div>
          <button onClick={getInsights} disabled={insightsLoading} data-testid="nexa-finance-insights-btn"
            className="text-[11px] text-sky-400 active:scale-95">{insightsLoading ? 'Thinking…' : 'Find leaks'}</button>
        </div>
        <p className="text-[13px] text-white/70 leading-relaxed whitespace-pre-wrap" data-testid="nexa-finance-insights">
          {insights || 'Tap “Find leaks” and I’ll look at where your money goes and spot where it’s slipping away.'}
        </p>
      </div>

      {/* Category breakdown */}
      {Object.keys(s.by_category).length > 0 && (
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
          <p className="text-[11px] font-mono tracking-[0.2em] uppercase text-white/50 mb-3">Where it goes</p>
          <div className="space-y-2.5">
            {Object.entries(s.by_category).map(([cat, amt], i) => (
              <div key={cat}>
                <div className="flex justify-between text-[12.5px] mb-1"><span className="text-white/70">{cat}</span><span className="text-white/90 font-medium">{c}{fmt(amt)}</span></div>
                <div className="h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${(amt / maxCat) * 100}%`, background: CAT_COLORS[i % CAT_COLORS.length] }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* end left analytics column */}
      </div>
      {/* right ledger column */}
      <div className="xl:col-span-7 flex flex-col gap-5 min-w-0">

      {/* Transactions */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-[11px] font-mono tracking-[0.2em] uppercase text-white/50">
            Transactions · {s.count}
            {month && (
              <button onClick={() => { setMonth(''); load('', scope); }} data-testid="nexa-month-clear"
                className="ml-2 normal-case tracking-normal text-sky-300 hover:text-sky-200">
                {monthName(month)} ✕
              </button>
            )}
            {catFilter && (
              <button onClick={() => setCatFilter(null)} data-testid="nexa-cat-clear"
                className="ml-2 normal-case tracking-normal text-violet-300 hover:text-violet-200">
                {catFilter} ✕
              </button>
            )}
          </p>
          <div className="flex items-center gap-3">
            <button onClick={() => setShowFilters((v) => !v)} data-testid="nexa-filters-toggle"
              className={`flex items-center gap-1 text-[11px] active:scale-95 ${showFilters || dateFrom || dateTo || search || catFilter ? 'text-violet-300' : 'text-white/50 hover:text-white/80'}`}>
              <SlidersHorizontal className="w-3.5 h-3.5" /> Filter
            </button>
            <button onClick={() => setShowAdd((v) => !v)} data-testid="nexa-finance-add-toggle"
              className="flex items-center gap-1 text-[11px] text-sky-400 active:scale-95"><Plus className="w-3.5 h-3.5" /> Add</button>
          </div>
        </div>
        {showFilters && (
          <div className="mb-3 rounded-xl border border-white/[0.08] bg-white/[0.02] p-3 space-y-2.5" data-testid="nexa-filters-panel">
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search description…"
              data-testid="nexa-filter-search"
              className="w-full bg-black border border-white/15 rounded-lg text-[12px] text-white/85 px-2.5 py-2 outline-none focus:border-sky-400/50" />
            <div className="flex items-center gap-2">
              <span className="text-[11px] text-white/45 w-14 shrink-0">Category</span>
              <select value={catFilter || ''} onChange={(e) => setCatFilter(e.target.value || null)}
                data-testid="nexa-filter-category"
                className="flex-1 bg-black border border-white/15 rounded-lg text-[12px] text-white/85 px-2 py-1.5 outline-none">
                <option value="" className="bg-black">All categories</option>
                {(data.categories || []).map((c) => <option key={c} value={c} className="bg-black">{c}</option>)}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[11px] text-white/45 w-14 shrink-0">From</span>
              <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                data-testid="nexa-filter-date-from"
                className="flex-1 bg-black border border-white/15 rounded-lg text-[12px] text-white/85 px-2 py-1.5 outline-none [color-scheme:dark]" />
              <span className="text-[11px] text-white/45 shrink-0">To</span>
              <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                data-testid="nexa-filter-date-to"
                className="flex-1 bg-black border border-white/15 rounded-lg text-[12px] text-white/85 px-2 py-1.5 outline-none [color-scheme:dark]" />
            </div>
            {(search || catFilter || dateFrom || dateTo) && (
              <button onClick={() => { setSearch(''); setCatFilter(null); setDateFrom(''); setDateTo(''); }}
                data-testid="nexa-filter-clear-all"
                className="text-[11px] text-white/60 hover:text-white/90">Clear all filters</button>
            )}
          </div>
        )}
        {(() => {
          const uncat = data.transactions.filter((t) => t.category === 'Uncategorized');
          if (uncat.length === 0 && !reviewOnly) return null;
          return (
            <div className="flex items-center gap-2 mb-2.5 flex-wrap" data-testid="nexa-review-bar">
              <button onClick={() => setReviewOnly((v) => !v)} data-testid="nexa-review-filter"
                className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${reviewOnly ? 'bg-amber-400/15 border-amber-400/40 text-amber-200' : 'bg-white/[0.04] border-white/10 text-white/60 hover:text-white/85'}`}>
                {reviewOnly ? 'Showing to review' : `To review (${uncat.length})`}
              </button>
              {reviewOnly && (
                <button onClick={() => setReviewOnly(false)} data-testid="nexa-review-clear"
                  className="text-[11px] text-white/45 hover:text-white/70">Show all</button>
              )}
              {uncat.length > 0 && (
                <button onClick={askNexaReview} data-testid="nexa-review-ask"
                  className="text-[11px] px-2.5 py-1 rounded-full border border-sky-400/30 bg-sky-400/10 text-sky-200 active:scale-95 ml-auto">
                  Ask Nexa in chat
                </button>
              )}
            </div>
          );
        })()}
        {showAdd && <ManualAdd categories={data.categories} defaultScope={scope === 'all' ? 'personal' : scope} onCreateCategory={createCategory} onAdded={() => { setShowAdd(false); setMonth(''); load('', scope); }} />}
        <div className="space-y-2">
          {data.transactions.length === 0 && (
            <p className="text-[12px] text-white/30 py-4">No transactions yet — send a screenshot of your bank or card and I’ll sort it out.</p>
          )}
          {(() => {
            const anyFilter = reviewOnly || catFilter || dateFrom || dateTo || search.trim();
            if (data.transactions.length === 0 || !anyFilter) return null;
            let list = data.transactions;
            if (reviewOnly) list = list.filter((t) => t.category === 'Uncategorized');
            if (catFilter) list = list.filter((t) => t.category === catFilter);
            if (dateFrom) list = list.filter((t) => (t.date || '') >= dateFrom);
            if (dateTo) list = list.filter((t) => (t.date || '') <= dateTo);
            if (search.trim()) { const q = search.trim().toLowerCase(); list = list.filter((t) => (t.description || '').toLowerCase().includes(q)); }
            return list.length === 0
              ? <p className="text-[12px] text-white/30 py-4" data-testid="nexa-txn-no-match">No transactions match these filters.</p>
              : <p className="text-[11px] text-white/35 pb-1" data-testid="nexa-txn-filter-count">{list.length} match{list.length === 1 ? '' : 'es'}</p>;
          })()}
          {(() => {
            let list = data.transactions;
            if (reviewOnly) list = list.filter((t) => t.category === 'Uncategorized');
            if (catFilter) list = list.filter((t) => t.category === catFilter);
            if (dateFrom) list = list.filter((t) => (t.date || '') >= dateFrom);
            if (dateTo) list = list.filter((t) => (t.date || '') <= dateTo);
            if (search.trim()) {
              const q = search.trim().toLowerCase();
              list = list.filter((t) => (t.description || '').toLowerCase().includes(q));
            }
            return [...list].sort((a, b) => (b.date || '').localeCompare(a.date || ''));
          })().map((t) => (
            <div key={t.id} className="rounded-xl border border-white/[0.07] bg-white/[0.02] p-3 flex items-center gap-3" data-testid={`nexa-txn-${t.id}`}>
              <div className="min-w-0 flex-1">
                <p className="text-[13px] text-white/85 truncate">{t.description}</p>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-[11px] text-white/35">{dmy(t.date)}</span>
                  <select value={t.category} onChange={(e) => onCatSelect(e.target.value, (c) => changeCat(t, c))}
                    data-testid={`nexa-txn-cat-${t.id}`}
                    className="text-[10.5px] bg-white/[0.05] border border-white/10 rounded-full px-2 py-0.5 text-white/60 outline-none">
                    {data.categories.map((cat) => <option key={cat} value={cat} className="bg-black">{cat}</option>)}
                    <option value={NEW_CAT} className="bg-black text-sky-300">＋ New category…</option>
                  </select>
                </div>
              </div>
              <div className="flex flex-col items-end shrink-0">
                <button onClick={() => toggleType(t)} data-testid={`nexa-txn-type-${t.id}`}
                  className={`text-[14px] font-semibold ${t.type === 'income' ? 'text-emerald-400' : 'text-rose-300'}`}>
                  {t.type === 'income' ? '+' : '−'}{cur(t.currency)}{fmt(t.amount)}
                </button>
                {(t.currency || 'ILS').toUpperCase() !== 'ILS' && (
                  <button onClick={() => editRate(t)} data-testid={`nexa-txn-rate-${t.id}`}
                    title="Tap to set the ₪ your bank charged"
                    className="text-[10.5px] text-sky-300/70 hover:text-sky-200 mt-0.5">
                    ≈ ₪{fmt(t.amount_ils != null ? t.amount_ils : t.amount)} ✎
                  </button>
                )}
              </div>
              {t.src_import && (
                <button onClick={() => viewSource(t)} data-testid={`nexa-txn-source-${t.id}`}
                  title="See where this came from"
                  className="w-7 h-7 rounded-lg border border-white/10 flex items-center justify-center text-sky-300/70 hover:text-sky-200 shrink-0 active:scale-90">
                  <Eye className="w-3.5 h-3.5" strokeWidth={2} />
                </button>
              )}
              <button onClick={() => remove(t.id)} data-testid={`nexa-txn-del-${t.id}`}
                className="w-7 h-7 rounded-lg border border-white/10 flex items-center justify-center text-rose-300/60 shrink-0 active:scale-90">
                <Trash2 className="w-3.5 h-3.5" strokeWidth={2} />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* end right ledger column */}
      </div>
      </div>{/* end bento grid */}

      {/* Settings sheet — vendor rules & category manager (moved out of the main flow) */}
      <Sheet open={showSettings} onOpenChange={setShowSettings}>
        <SheetContent side="right" className="bg-[#0b0b0f] border-l border-white/10 text-white w-full sm:max-w-md overflow-y-auto" data-testid="nexa-finance-settings-sheet">
          <SheetHeader>
            <SheetTitle className="text-white/90 text-[15px] font-semibold">Rules & categories</SheetTitle>
          </SheetHeader>
          <div className="mt-5 space-y-4">
            {/* Vendor rules */}
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4" data-testid="nexa-finance-rules">
              <div className="flex items-center justify-between mb-1">
                <button onClick={() => setShowRules((v) => !v)} data-testid="nexa-rules-toggle"
                  className="text-[11px] font-mono tracking-[0.2em] uppercase text-white/50 flex items-center gap-1.5">
                  Vendor rules{rules.length ? ` (${rules.length})` : ''}
                </button>
                <button onClick={addRule} data-testid="nexa-rule-add" className="flex items-center gap-1 text-[11px] text-sky-400 active:scale-95"><Plus className="w-3.5 h-3.5" /> Add</button>
              </div>
              <p className="text-[11px] text-white/35 leading-relaxed">
                Teach me once — "vendors containing X go to category Y" — and every future import files them automatically.
              </p>
              {rules.length > 0 && (
                <div className="space-y-1.5 mt-3">
                  {rules.map((r) => (
                    <div key={r.id} data-testid={`nexa-rule-${r.id}`} className="flex items-center justify-between rounded-xl border border-white/[0.07] bg-white/[0.02] px-3 py-2">
                      <span className="text-[12.5px] text-white/75 truncate">
                        <span className="text-white/45">"{r.match}"</span> → {r.category}
                      </span>
                      <button onClick={() => removeRule(r.id)} data-testid={`nexa-rule-del-${r.id}`} className="text-white/30 hover:text-rose-300 shrink-0"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Manage categories */}
            {catInfo.length > 0 && (
              <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4" data-testid="nexa-finance-catmgr">
                <p className="text-[11px] font-mono tracking-[0.2em] uppercase text-white/50 mb-1">Manage categories ({catInfo.length})</p>
                <p className="text-[11px] text-white/35 leading-relaxed">Sorted A→Z. Rename or merge your own categories, and delete ones you no longer use.</p>
                <div className="space-y-2 mt-3">
                  {catInfo.map((c) => (
                    <div key={c.name} data-testid={`nexa-catmgr-${c.name}`} className="rounded-xl border border-white/[0.07] bg-white/[0.02] px-3 py-2">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-[12.5px] text-white/80 truncate">
                          {c.name} <span className="text-white/35">· {c.count} txn{c.count === 1 ? '' : 's'}</span>
                          {c.custom && <span className="ml-1.5 text-[9px] px-1.5 py-0.5 rounded-full bg-sky-400/15 text-sky-300 align-middle">yours</span>}
                        </span>
                        <div className="flex items-center gap-3 shrink-0">
                          {c.custom && <button onClick={() => renameCat(c.name)} data-testid={`nexa-catmgr-rename-${c.name}`} className="text-[11px] text-sky-300 hover:text-sky-200">Rename</button>}
                          {c.deletable && <button onClick={() => deleteCat(c)} data-testid={`nexa-catmgr-del-${c.name}`} className="text-[11px] text-rose-300/70 hover:text-rose-300">Delete</button>}
                        </div>
                      </div>
                      {c.custom && (
                        <div className="flex items-center gap-2 mt-1.5">
                          <span className="text-[10.5px] text-white/40 shrink-0">Merge into</span>
                          <select defaultValue="" onChange={(e) => { if (e.target.value) { mergeCat(c.name, e.target.value); e.target.value = ''; } }}
                            data-testid={`nexa-catmgr-merge-${c.name}`}
                            className="bg-black border border-white/15 rounded-lg text-[11px] text-white/80 px-2 py-1">
                            <option value="" disabled className="bg-black">Choose…</option>
                            {(data.categories || []).filter((x) => x !== c.name).map((x) => <option key={x} value={x} className="bg-black">{x}</option>)}
                          </select>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* Category cascade — offer to switch other lines with the same name */}
      {cascade && (
        <div className="fixed inset-0 z-[72] bg-black/85 backdrop-blur-sm flex items-center justify-center p-3 sm:p-6" data-testid="nexa-cat-cascade">
          <div className="w-full max-w-lg max-h-[90vh] flex flex-col rounded-2xl border border-white/12 bg-[#0b0b0f]">
            <div className="flex items-start justify-between px-4 py-3 border-b border-white/10">
              <div className="pr-3">
                <p className="text-[13.5px] font-semibold text-white/90">Switch the others too?</p>
                <p className="text-[11px] text-white/45 mt-0.5 leading-relaxed">
                  {cascade.matches.length} other line{cascade.matches.length > 1 ? 's' : ''} named <span className="text-white/70">“{cascade.name}”</span> {cascade.matches.length > 1 ? 'have' : 'has'} a different category. Tick the ones to move to <span className="text-white/80 font-medium">{cascade.category}</span>.
                </p>
              </div>
              <button onClick={() => setCascade(null)} data-testid="nexa-cascade-close"
                className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-white/80 hover:bg-white/20 shrink-0"><X className="w-4 h-4" /></button>
            </div>
            <div className="flex-1 overflow-y-auto px-4 py-3 space-y-1.5">
              {cascade.matches.map((m) => {
                const on = cascade.selected.has(m.id);
                return (
                  <button key={m.id} onClick={() => toggleCascade(m.id)} data-testid={`nexa-cascade-item-${m.id}`}
                    className={`w-full flex items-center gap-3 rounded-xl border px-3 py-2 text-left transition-colors ${on ? 'border-sky-400/40 bg-sky-400/[0.07]' : 'border-white/[0.07] bg-white/[0.02]'}`}>
                    <span className={`w-4 h-4 rounded-[5px] border flex items-center justify-center shrink-0 ${on ? 'bg-sky-400 border-sky-400' : 'border-white/25'}`}>
                      {on && <Check className="w-3 h-3 text-black" />}
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="block text-[12.5px] text-white/85 truncate">{m.description}</span>
                      <span className="block text-[10.5px] text-white/40">{dmy(m.date)} · {scopeMeta(m.scope).label} · now {m.category}</span>
                    </span>
                    <span className="text-[12px] font-medium text-white/70 shrink-0">{cur(m.currency)}{fmt(m.amount)}</span>
                  </button>
                );
              })}
            </div>
            <div className="flex gap-2 px-4 py-3 border-t border-white/10">
              <button onClick={() => setCascade(null)} data-testid="nexa-cascade-skip"
                className="flex-1 h-10 rounded-xl border border-white/15 text-[13px] text-white/70 hover:bg-white/5">Keep as is</button>
              <button onClick={applyCascade} data-testid="nexa-cascade-apply"
                className="flex-1 h-10 rounded-xl bg-sky-400 text-black font-semibold text-[13px] disabled:opacity-40 active:scale-[0.98]" disabled={cascade.selected.size === 0}>
                Update {cascade.selected.size} selected
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Import recap — the summary Lev asked for at the end of the process */}
      {recap && (
        <div className="fixed inset-0 z-[75] bg-black/85 backdrop-blur-sm flex items-center justify-center p-3 sm:p-6" data-testid="nexa-import-recap">
          <div className="w-full max-w-md rounded-2xl border border-white/12 bg-[#0b0b0f] overflow-hidden">
            <div className="px-5 pt-5 pb-3 flex items-start gap-3">
              <div className="w-9 h-9 rounded-full bg-emerald-400/15 flex items-center justify-center shrink-0">
                <Sparkles className="w-4.5 h-4.5 text-emerald-300" />
              </div>
              <div className="min-w-0">
                <p className="text-[14px] font-semibold text-white/90">All done — here’s the recap</p>
                <p className="text-[11.5px] text-white/45 mt-0.5">I added <span className="text-white/80 font-medium">{recap.count}</span> transaction{recap.count === 1 ? '' : 's'} from this import.</p>
              </div>
            </div>

            <div className="px-5 grid grid-cols-3 gap-2">
              <div className="rounded-xl bg-emerald-400/[0.06] border border-emerald-400/15 p-2.5 text-center">
                <p className="text-[9.5px] font-mono uppercase tracking-wider text-emerald-300/70">Income</p>
                <p className="text-[14px] font-semibold text-emerald-300 mt-1">₪{fmt(recap.income)}</p>
              </div>
              <div className="rounded-xl bg-rose-400/[0.06] border border-rose-400/15 p-2.5 text-center">
                <p className="text-[9.5px] font-mono uppercase tracking-wider text-rose-300/70">Spent</p>
                <p className="text-[14px] font-semibold text-rose-300 mt-1">₪{fmt(recap.expense)}</p>
              </div>
              <div className="rounded-xl bg-white/[0.04] border border-white/10 p-2.5 text-center">
                <p className="text-[9.5px] font-mono uppercase tracking-wider text-white/45">Net</p>
                <p className={`text-[14px] font-semibold mt-1 ${recap.net >= 0 ? 'text-emerald-300' : 'text-rose-300'}`}>{recap.net >= 0 ? '+' : '−'}₪{fmt(Math.abs(recap.net))}</p>
              </div>
            </div>

            <div className="px-5 mt-3 flex gap-2">
              {SCOPES.map((s) => (recap.scope?.[s]?.count > 0 && (
                <div key={s} className={`flex-1 rounded-lg border px-2.5 py-1.5 ${scopeMeta(s).card}`}>
                  <span className={`text-[10px] ${scopeMeta(s).text}`}>{scopeMeta(s).label}</span>
                  <p className="text-[11.5px] text-white/70 mt-0.5">{recap.scope[s].count} item{recap.scope[s].count === 1 ? '' : 's'} · ₪{fmt(recap.scope[s].expense)}</p>
                </div>
              )))}
            </div>

            {recap.top && recap.top.length > 0 && (
              <div className="px-5 mt-3">
                <p className="text-[10px] font-mono uppercase tracking-wider text-white/40 mb-1.5">Top categories</p>
                <div className="space-y-1">
                  {recap.top.map((t) => (
                    <div key={t.category} className="flex items-center justify-between text-[12px]">
                      <span className="text-white/70 truncate pr-2">{t.category}</span>
                      <span className="text-white/85 font-medium shrink-0">₪{fmt(t.amount)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="px-5 py-4 mt-2">
              <button onClick={() => setRecap(null)} data-testid="nexa-recap-close"
                className="w-full h-11 rounded-xl bg-white text-black font-semibold text-[13px] active:scale-[0.98]">Got it</button>
            </div>
          </div>
        </div>
      )}

      {/* Import review — confirm duplicates & set categories before saving */}
      {pendingReview && pendingReview.items.length > 0 && (
        <div className="fixed inset-0 z-[70] bg-black/85 backdrop-blur-sm flex items-center justify-center p-3 sm:p-6" data-testid="nexa-pending-review">
          <div className="w-full max-w-2xl max-h-[90vh] flex flex-col rounded-2xl border border-white/12 bg-[#0b0b0f]">
            <div className="flex items-start justify-between px-4 py-3 border-b border-white/10">
              <div className="pr-3">
                <p className="text-[13.5px] font-semibold text-white/90">Review before I save</p>
                <p className="text-[11px] text-white/45 mt-0.5 leading-relaxed">
                  {pendingReview.items.length} item{pendingReview.items.length > 1 ? 's' : ''} — I found possible duplicates or wasn’t sure how to file them. Nothing’s saved until you decide.
                </p>
              </div>
              <button onClick={() => setPendingReview(null)} data-testid="nexa-pending-close"
                className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-white/80 hover:bg-white/20 shrink-0">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
              {pendingReview.items.map((item) => (
                <div key={item.id} data-testid={`nexa-pending-${item.id}`}
                  className={`rounded-xl border p-3 ${item.kind === 'duplicate' ? 'border-amber-400/25 bg-amber-400/[0.05]' : 'border-sky-400/25 bg-sky-400/[0.05]'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className={`text-[10.5px] font-mono uppercase tracking-[0.15em] ${item.kind === 'duplicate' ? 'text-amber-200/80' : 'text-sky-200/80'}`}>
                      {item.kind === 'duplicate'
                        ? `Possible duplicate · ~${Math.round((item.score || 0) * 100)}% match`
                        : 'Not sure how to categorise'}
                    </span>
                  </div>

                  {item.kind === 'duplicate' ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <PendingTxnCard label="New — this import" t={item.new} />
                      {item.match && <PendingTxnCard label="Already in your books" t={item.match} />}
                    </div>
                  ) : (
                    <>
                      <PendingTxnCard label="New — this import" t={item.new} />
                      {(() => {
                        const norm = (s) => (s || '').trim().toLowerCase();
                        const dup = pendingReview.items.filter((x) => x.kind === 'uncertain'
                          && norm(x.new?.description) === norm(item.new?.description)).length;
                        return dup > 1 ? (
                          <p className="text-[10.5px] text-sky-200/70 mt-2" data-testid={`nexa-pending-identical-${item.id}`}>
                            Appears {dup}× in this import — picking a category files all {dup} at once.
                          </p>
                        ) : null;
                      })()}
                      <div className="flex items-center gap-2 mt-2">
                        <span className="text-[11px] text-white/50 shrink-0">Category</span>
                        <select value={pendingCats[item.id] || (item.new.category && item.new.category !== 'Uncategorized' ? item.new.category : '')}
                          onChange={(e) => onCatSelect(e.target.value, (c) => setPendingCats((m) => ({ ...m, [item.id]: c })))}
                          data-testid={`nexa-pending-cat-${item.id}`}
                          className="flex-1 bg-black border border-white/15 rounded-lg text-[12px] text-white/85 px-2 py-1.5">
                          <option value="" disabled className="bg-black">Pick a category…</option>
                          {(data.categories || []).filter((c) => c !== 'Uncategorized').map((c) => <option key={c} value={c} className="bg-black">{c}</option>)}
                          <option value={NEW_CAT} className="bg-black text-sky-300">＋ New category…</option>
                        </select>
                      </div>
                    </>
                  )}

                  <div className="flex gap-2 mt-3">
                    <button onClick={() => resolvePending(item, 'skip')} data-testid={`nexa-pending-skip-${item.id}`}
                      className="flex-1 h-9 rounded-lg border border-white/15 text-[12.5px] text-white/70 hover:bg-white/5 active:scale-[0.98]">
                      {item.kind === 'duplicate' ? "Skip — it's a duplicate" : 'Skip'}
                    </button>
                    <button onClick={() => resolvePending(item, 'add')} data-testid={`nexa-pending-add-${item.id}`}
                      disabled={item.kind === 'uncertain' && !pendingCats[item.id] && (!item.new.category || item.new.category === 'Uncategorized')}
                      className="flex-1 h-9 rounded-lg bg-sky-400 text-black font-semibold text-[12.5px] disabled:opacity-40 active:scale-[0.98]">
                      {item.kind === 'duplicate' ? 'Add anyway' : 'Add it'}
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex gap-2 px-4 py-3 border-t border-white/10">
              <button onClick={() => resolveAllPending('skip')} data-testid="nexa-pending-skip-all"
                className="flex-1 h-10 rounded-xl border border-white/15 text-[13px] text-white/70 hover:bg-white/5">Skip all</button>
              <button onClick={() => resolveAllPending('add')} data-testid="nexa-pending-add-all"
                className="flex-1 h-10 rounded-xl bg-white/10 text-[13px] text-white/85 hover:bg-white/15">Add all</button>
            </div>
          </div>
        </div>
      )}

      {/* Source viewer lightbox */}
      {sourceView && (
        <div className="fixed inset-0 z-[60] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setSourceView(null)} data-testid="nexa-source-lightbox">
          <button onClick={() => setSourceView(null)} data-testid="nexa-source-close"
            className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/10 flex items-center justify-center text-white/80 hover:bg-white/20">
            <X className="w-4 h-4" />
          </button>
          <div className="max-w-3xl w-full max-h-[88vh] flex flex-col items-center gap-3" onClick={(e) => e.stopPropagation()}>
            {sourceView.label && (
              <p className="text-[12px] text-white/70">
                {sourceView.label}{sourceView.kind === 'pdf' && sourceView.page ? ` · page ${sourceView.page}` : ''}
              </p>
            )}
            {sourceView.loading ? (
              <Loader2 className="w-6 h-6 animate-spin text-sky-400" />
            ) : (
              <img src={sourceView.image} alt="source" data-testid="nexa-source-image"
                className="max-h-[82vh] w-auto rounded-xl border border-white/15 bg-white object-contain" />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const ManualAdd = ({ categories, onAdded, defaultScope = 'personal', onCreateCategory }) => {
  const [f, setF] = useState({ description: '', amount: '', date: new Date().toISOString().slice(0, 10), type: 'expense', category: 'Other', currency: 'ILS', scope: defaultScope });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!f.description.trim() || !f.amount) { toast.error('Add a description and amount'); return; }
    setSaving(true);
    try {
      await nexaApi.post('/finance/transaction', { ...f, amount: parseFloat(f.amount) });
      toast.success('Added');
      onAdded();
    } catch { toast.error('Could not add'); }
    finally { setSaving(false); }
  };
  const inp = 'bg-white/[0.05] border border-white/10 rounded-lg px-2.5 py-2 text-[13px] text-white outline-none focus:border-sky-400/50';
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3 mb-3 space-y-2" data-testid="nexa-finance-manual">
      <div className="grid grid-cols-2 gap-2">
        <input placeholder="Description" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className={inp} data-testid="nexa-finance-manual-desc" />
        <input placeholder="Amount" type="number" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} className={inp} data-testid="nexa-finance-manual-amount" />
        <input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} className={inp} />
        <select value={f.category} onChange={async (e) => {
          if (e.target.value === NEW_CAT) {
            const created = onCreateCategory ? await onCreateCategory(window.prompt('New category name:')) : null;
            if (created) setF((p) => ({ ...p, category: created }));
            return;
          }
          setF({ ...f, category: e.target.value });
        }} className={inp}>
          {categories.map((cat) => <option key={cat} value={cat} className="bg-black">{cat}</option>)}
          <option value={NEW_CAT} className="bg-black text-sky-300">＋ New category…</option>
        </select>
      </div>
      <div className="flex gap-2">
        <button onClick={() => setF({ ...f, scope: SCOPES[(SCOPES.indexOf(f.scope) + 1) % SCOPES.length] })}
          data-testid="nexa-finance-manual-scope"
          title="Tap to switch account"
          className={`flex-1 h-9 rounded-lg text-[13px] font-medium ${scopeMeta(f.scope).pill}`}>
          {scopeMeta(f.scope).label}
        </button>
        <button onClick={() => setF({ ...f, type: f.type === 'expense' ? 'income' : 'expense' })}
          className={`flex-1 h-9 rounded-lg text-[13px] font-medium ${f.type === 'income' ? 'bg-emerald-400/15 text-emerald-300' : 'bg-rose-400/15 text-rose-300'}`}>
          {f.type === 'income' ? 'Income' : 'Expense'}
        </button>
        <button onClick={save} disabled={saving} data-testid="nexa-finance-manual-save"
          className="flex-1 h-9 rounded-lg bg-sky-400 text-black font-semibold text-[13px]">{saving ? '…' : 'Save'}</button>
      </div>
    </div>
  );
};
